/** Longest message we put in a toast; a model-binding error can run to several lines. */
const MAX_MESSAGE_LENGTH = 300;

/**
 * Pulls the human-readable reason out of a failed API response.
 *
 * The API reports a rejected write in three different shapes, and a caller that
 * shows only its own generic message throws all three away:
 *
 *   - `ValidationProblemDetails` — `{ errors: { field: [message, ...] } }`, what
 *     `[ApiController]` returns automatically for a failed model binding or a
 *     `[Required]`/`[Range]` violation.
 *   - `{ error: '...' }` — the ad-hoc shape controllers use for a business-rule
 *     rejection, e.g. a board save naming a director that was merged away.
 *   - `ApiErrorResponse` — `{ errorCode, message }` from the ControllerExtensions
 *     helpers.
 *
 * Discarding them is what left users clicking Save against a validation error the
 * response had already named: the employees tab sent the same rejected payload 13
 * times in 5 seconds because "Failed to save Employees" gave them nothing to act on.
 */
export function serverErrorMessage(error: unknown, fallback: string): string {
  const body = (error as { error?: unknown } | null)?.error;

  if (typeof body === 'string') {
    return clamp(body, fallback);
  }

  if (!body || typeof body !== 'object') {
    return fallback;
  }

  const payload = body as {
    errors?: unknown;
    error?: unknown;
    message?: unknown;
    title?: unknown;
  };

  return clamp(
    formatValidationErrors(payload.errors)
      ?? firstString(payload.error, payload.message, payload.title)
      ?? '',
    fallback
  );
}

/**
 * Flattens a `ValidationProblemDetails.errors` map into one sentence.
 *
 * The key is kept: a model-binding failure keys its message by JSON path
 * (`$.total`), and naming the field is the only thing that makes an otherwise
 * generic "could not be converted" message actionable.
 */
function formatValidationErrors(errors: unknown): string | null {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  const parts: string[] = [];

  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    const messages = (Array.isArray(value) ? value : [value])
      .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
      .map(m => m.trim());

    if (messages.length === 0) {
      continue;
    }

    const field = key.replace(/^\$\.?/, '').trim();
    parts.push(field ? `${field}: ${messages.join(' ')}` : messages.join(' '));
  }

  return parts.length > 0 ? parts.join(' — ') : null;
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
}

function clamp(message: string, fallback: string): string {
  const trimmed = message.trim();

  if (!trimmed) {
    return fallback;
  }

  return trimmed.length <= MAX_MESSAGE_LENGTH
    ? trimmed
    : `${trimmed.slice(0, MAX_MESSAGE_LENGTH - 1)}…`;
}
